import React, { useEffect, useMemo, useRef, useState } from 'react';
import { CalendarClock, CalendarDays, List as ListIcon, X } from 'lucide-react';
import {
  type AgendaBlock,
  type EventAgenda,
  buildDayHeaders,
  CATEGORY_BY_KEY,
  timeToMinutes,
} from '@/routes/tools/research-proposal/components/WizardSteps/Step4Proposal/AgendaManager/constants';
import { AGENDA_BLOCK_GAP, agendaBlockColor, agendaBlockDensity, agendaDayWidth, agendaCalendarWindow, agendaTime, blockEnd, blockTimeRange, layoutAgendaBlocks, sortAgendaBlocks } from './agendaPresentation';
import './agenda.css';

interface AgendaProps { agenda: EventAgenda | null | undefined }
type ViewMode = 'calendar' | 'schedule';
type Headers = ReturnType<typeof buildDayHeaders>;

export function Agenda({ agenda }: AgendaProps) {
  const headers = useMemo(() => agenda ? buildDayHeaders(agenda.day_mode, agenda.start_date, agenda.day_count) : [], [agenda]);
  const [view, setView] = useState<ViewMode>('calendar');
  const [activeDay, setActiveDay] = useState(1);
  const [selected, setSelected] = useState<AgendaBlock | null>(null);
  const scheduleHeading = useRef<HTMLHeadingElement>(null);
  const focusSchedule = useRef(false);
  const day = headers.some(header => header.dayNumber === activeDay) ? activeDay : 1;
  const blocksByDay = useMemo(() => new Map(headers.map(header => [header.dayNumber,
    sortAgendaBlocks((agenda?.blocks || []).filter(block => block.day_number === header.dayNumber)),
  ])), [agenda, headers]);
  const window = useMemo(() => agendaCalendarWindow(agenda?.blocks || []), [agenda]);

  useEffect(() => {
    if (view === 'schedule' && focusSchedule.current) {
      scheduleHeading.current?.focus();
      focusSchedule.current = false;
    }
  }, [view, day]);
  const openDaySchedule = (dayNumber: number) => {
    focusSchedule.current = true;
    setActiveDay(dayNumber);
    setView('schedule');
  };

  if (!agenda || agenda.blocks.length === 0) {
    return <div className="proposal-agenda agenda-empty"><div><CalendarClock aria-hidden="true" size={36} /><p>The agenda is being finalized.</p></div></div>;
  }

  return (
    <div className="proposal-agenda" data-agenda-design="rebel-compact">
      <div className="agenda-content">
        <header className="agenda-header">
          <div>
            <h1>{agenda.title || 'Preliminary Agenda'}</h1>
            <p>A high-level look at how your event could flow. Timings and details are indicative.</p>
          </div>
          <div className="agenda-view-switch" role="group" aria-label="Agenda view">
            {([{ key: 'calendar', label: 'Calendar', Icon: CalendarDays }, { key: 'schedule', label: 'Schedule', Icon: ListIcon }] as const).map(({ key, label, Icon }) => (
              <button key={key} type="button" aria-pressed={view === key} onClick={() => setView(key)}><Icon size={16} aria-hidden="true" />{label}</button>
            ))}
          </div>
        </header>
        {/* Category colours remain on items; Tracey requested no top-level legend. */}
        <div className={view === 'calendar' ? 'agenda-mobile-days' : undefined}>
          <DayTabs headers={headers} activeDay={day} onSelectDay={setActiveDay} />
        </div>
        {view === 'calendar' ? (
          <CalendarView headers={headers} blocksByDay={blocksByDay} activeDay={day} window={window} onSelect={setSelected} onOpenDay={openDaySchedule} />
        ) : (
          <ScheduleView blocks={blocksByDay.get(day) || []} header={headers.find(header => header.dayNumber === day)} onSelect={setSelected} headingRef={scheduleHeading} />
        )}
        <p className="agenda-hint">Select an item for full details. Short blocks use compact markers; select a day heading for its full schedule.</p>
      </div>
      <BlockDetails block={selected} onClose={() => setSelected(null)} />
    </div>
  );
}

function DayTabs({ headers, activeDay, onSelectDay }: { headers: Headers; activeDay: number; onSelectDay: (day: number) => void }) {
  return <div className="agenda-day-tabs" role="group" aria-label="Agenda day">
    {headers.map(header => <button key={header.dayNumber} type="button" aria-pressed={activeDay === header.dayNumber} onClick={() => onSelectDay(header.dayNumber)}>
      <strong>{header.label}</strong>{header.sublabel && <span>{header.sublabel}</span>}
    </button>)}
  </div>;
}

function blockStyle(block: AgendaBlock): React.CSSProperties {
  const color = agendaBlockColor(block);
  return { '--agenda-category': color } as React.CSSProperties;
}

function ScheduleView({ blocks, header, onSelect, headingRef }: { blocks: AgendaBlock[]; header?: Headers[number]; onSelect: (block: AgendaBlock) => void; headingRef: React.RefObject<HTMLHeadingElement | null> }) {
  return <section className="agenda-schedule" aria-label="Schedule">
    <h2 ref={headingRef} tabIndex={-1}>{header?.label}<span>{header?.sublabel}</span></h2>
    {blocks.length === 0 ? <p className="agenda-day-empty">No items scheduled for this day yet.</p> : <div className="agenda-schedule-list">
      {blocks.map(block => <button className="agenda-schedule-item" key={block.id} type="button" style={blockStyle(block)} onClick={() => onSelect(block)}>
        <strong>{block.name}</strong><span className="agenda-item-time">{blockTimeRange(block)}</span>
        {block.description && <p>{block.description}</p>}
      </button>)}
    </div>}
  </section>;
}

function CalendarView({ headers, blocksByDay, activeDay, window: win, onSelect, onOpenDay }: {
  headers: Headers; blocksByDay: Map<number, AgendaBlock[]>; activeDay: number;
  window: ReturnType<typeof agendaCalendarWindow>; onSelect: (block: AgendaBlock) => void; onOpenDay: (day: number) => void;
}) {
  const { startHour, endHour, hourHeight } = win;
  const hours = Array.from({ length: endHour - startHour + 1 }, (_, index) => startHour + index);
  const layouts = new Map(headers.map(header => [header.dayNumber, layoutAgendaBlocks(blocksByDay.get(header.dayNumber) || [])]));
  const laneCount = (day: number) => Math.max(1, ...(layouts.get(day) || []).map(item => item.lanes));
  const style = {
    '--agenda-columns': headers.map(header => `minmax(${agendaDayWidth(laneCount(header.dayNumber))}px, 1fr)`).join(' '),
    '--agenda-mobile-day-width': laneCount(activeDay) > 1 ? `${agendaDayWidth(laneCount(activeDay))}px` : '0px',
    '--agenda-hour': `${hourHeight}px`, '--agenda-height': `${(endHour - startHour) * hourHeight}px`,
  } as React.CSSProperties;
  return <div className="agenda-calendar-scroll" role="region" aria-label="Agenda calendar" tabIndex={0} style={style}>
    <div className="agenda-calendar">
      <div className="agenda-ruler" aria-hidden="true">
        <div className="agenda-ruler-heading">Time</div>
        <div className="agenda-ruler-body">{hours.map((hour, index) => <span key={hour} style={{ top: index * hourHeight }}>{agendaTime(`${hour}:00`)}</span>)}</div>
      </div>
      {headers.map(header => {
        const blocks = blocksByDay.get(header.dayNumber) || [];
        const layout = layouts.get(header.dayNumber) || [];
        return <section key={header.dayNumber} className={`agenda-day-column${header.dayNumber === activeDay ? ' is-active' : ''}`} aria-label={[header.label, header.sublabel].filter(Boolean).join(', ')}>
          <header className="agenda-day-heading"><button type="button" onClick={() => onOpenDay(header.dayNumber)} aria-label={`View ${[header.label, header.sublabel].filter(Boolean).join(', ')} full schedule`} title="View full day schedule">
            <strong>{header.label}</strong>{header.sublabel && <span>{header.sublabel}</span>}
            <span className="agenda-day-open" aria-hidden="true">↗</span>
          </button></header>
          <div className="agenda-day-grid">
            {blocks.length === 0 && <p className="agenda-day-empty">No items scheduled yet.</p>}
            {layout.map(({ block, lane, lanes }) => <button key={block.id} type="button" className={`agenda-calendar-item is-${agendaBlockDensity(block)}`} style={{
              ...blockStyle(block),
              top: (timeToMinutes(block.start_time) - startHour * 60) * hourHeight / 60,
              height: Math.max(2, (blockEnd(block) - timeToMinutes(block.start_time)) * hourHeight / 60 - AGENDA_BLOCK_GAP),
              left: `calc(${lane * 100 / lanes}% + 4px)`, width: `calc(${100 / lanes}% - 8px)`,
            }} onClick={() => onSelect(block)} aria-label={`${block.name}, ${blockTimeRange(block)}. View details`} title={`${block.name} · ${blockTimeRange(block)}`}>
              {/* Do not enlarge a short event's duration or clip two lines into it.
                  Its full accessible name, tooltip, dialog and day Schedule keep every detail. */}
              {agendaBlockDensity(block) !== 'marker' && <strong>{block.name}</strong>}
              {['regular', 'compact'].includes(agendaBlockDensity(block)) && <span className="agenda-item-time">{blockTimeRange(block)}</span>}
            </button>)}
          </div>
        </section>;
      })}
    </div>
  </div>;
}

function BlockDetails({ block, onClose }: { block: AgendaBlock | null; onClose: () => void }) {
  const ref = useRef<HTMLDialogElement>(null);
  useEffect(() => {
    const dialog = ref.current;
    if (block && dialog && !dialog.open) dialog.showModal();
    else if (!block && dialog?.open) dialog.close();
  }, [block]);
  return <dialog ref={ref} className="agenda-details" aria-labelledby="agenda-detail-title" onClose={onClose}>
    {block && <><button type="button" className="agenda-detail-close" aria-label="Close item details" onClick={onClose}><X size={20} /></button>
      <p className="agenda-detail-category">{CATEGORY_BY_KEY[block.category_key]?.label || block.category_key}</p>
      <h2 id="agenda-detail-title">{block.name}</h2><p className="agenda-item-time">{blockTimeRange(block)}</p>
      {block.description && <p className="agenda-detail-description">{block.description}</p>}
    </>}
  </dialog>;
}

export default Agenda;
